import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import InternalLinkField from "@/components/admin/InternalLinkField";
import LinkableTextarea from "@/components/admin/LinkableTextarea";
import { toast } from "sonner";

/**
 * Generic CRUD page used for Services, Pricing, Add-ons, Reviews, FAQs.
 * Pass the resource (route segment), title, and field schema.
 */
export default function CrudPage({
  resource,
  title,
  description,
  fields,
  blank,
  testIdPrefix,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(blank);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${resource}`);
      setItems(data);
    } catch { toast.error("Load failed"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

  const updateField = (id, key, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [key]: value } : it)));
  };

  const save = async (item) => {
    try {
      await api.put(`/admin/${resource}/${item.id}`, item);
      toast.success("Saved");
    } catch (e) { toast.error(`Save failed: ${apiErrorMessage(e)}`); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name || item.title || item.question || item.customer_display_name}"?`)) return;
    try {
      await api.delete(`/admin/${resource}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const create = async () => {
    try {
      await api.post(`/admin/${resource}`, draft);
      toast.success("Added");
      setDraft(blank);
      setCreating(false);
      load();
    } catch (e) { toast.error(`Create failed: ${apiErrorMessage(e)}`); }
  };

  return (
    <div data-testid={`admin-${testIdPrefix}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-[#0F172A]">{title}</h2>
          {description && <p className="text-sm text-[#475569] mt-1">{description}</p>}
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid={`admin-${testIdPrefix}-add`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#1E40AF]"
        >
          <Plus className="w-4 h-4" /> Add new
        </button>
      </div>

      {creating && (
        <div className="mt-5 bg-white rounded-2xl border border-slate-100 p-5" data-testid={`admin-${testIdPrefix}-new-form`}>
          <h3 className="font-display font-semibold text-[#0F172A] mb-3">New {title.replace(/s$/, "")}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => (
              <FieldInput key={f.key} field={f} value={draft[f.key]} onChange={(v) => setDraft({ ...draft, [f.key]: v })} testid={`admin-${testIdPrefix}-new-${f.key}`} />
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => { setCreating(false); setDraft(blank); }} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold">Cancel</button>
            <button onClick={create} data-testid={`admin-${testIdPrefix}-new-save`} className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold">Save</button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="text-sm text-[#475569]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-sm text-[#475569]">Nothing here yet.</div>
        ) : items.map((it) => (
          <div key={it.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`admin-${testIdPrefix}-item-${it.id}`}>
            <div className="grid sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <FieldInput key={f.key} field={f} value={it[f.key]} onChange={(v) => updateField(it.id, f.key, v)} testid={`admin-${testIdPrefix}-item-${it.id}-${f.key}`} />
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => remove(it)} data-testid={`admin-${testIdPrefix}-item-${it.id}-delete`} className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-white border border-rose-200 text-rose-700 text-sm font-semibold hover:bg-rose-50">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
              <button onClick={() => save(it)} data-testid={`admin-${testIdPrefix}-item-${it.id}-save`} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#1E40AF]">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange, testid }) {
  const common = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]";
  const wrap = field.span === "full" ? "sm:col-span-2" : "";
  if (field.type === "internal_link") {
    return (
      <div className={wrap}>
        <InternalLinkField label={field.label} value={value || field.defaultValue || ""} onChange={onChange} testid={testid} help={field.help}
          allowEmpty={field.allowEmpty !== false} exclude={field.exclude || []} />
      </div>
    );
  }
  if (field.type === "linktext") {
    return (
      <div className={wrap}>
        <LinkableTextarea label={field.label} value={value} onChange={onChange} rows={field.rows || 3} testid={testid} />
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <label className={`block ${wrap}`}>
        <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{field.label}</span>
        <textarea data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={field.rows || 3} className={common} />
      </label>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className={`flex items-center gap-2 ${wrap}`}>
        <input data-testid={testid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
        <span className="text-sm font-medium text-[#0F172A]">{field.label}</span>
      </label>
    );
  }
  if (field.type === "number") {
    return (
      <label className={`block ${wrap}`}>
        <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{field.label}</span>
        <input data-testid={testid} type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={common} />
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className={`block ${wrap}`}>
        <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{field.label}</span>
        <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common}>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className={`block ${wrap}`}>
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{field.label}</span>
      <input data-testid={testid} type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common} />
    </label>
  );
}
